import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listMyMemberships, listOrganisations } from "../lib/api";
import type { MembershipRole, Organisation } from "../lib/types";
import { ROLE_LABEL } from "../lib/labels";
import { useBreadcrumb } from "../shell/ContextHeader";

// The organisations (clients) the signed-in consultant belongs to. RLS is the
// filter - the query asks for everything and receives only memberships. A
// consultant at LINNFOSS is routinely a member of several tenants; this is the
// screen where that is visible and deliberate (the cross-tenant hazards in
// 0001 §7 exist precisely because this list usually has more than one row).

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; orgs: Organisation[]; roles: Map<string, MembershipRole> };

export function ClientsList() {
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => setTrail([{ label: "Clients & engagements" }]), [setTrail]);

  useEffect(() => {
    Promise.all([listOrganisations(), listMyMemberships()])
      .then(([orgs, memberships]) =>
        setState({
          kind: "ready",
          orgs,
          roles: new Map(memberships.map((m) => [m.organisation_id, m.role])),
        }),
      )
      .catch((e: Error) => setState({ kind: "error", message: e.message }));
  }, []);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load organisations: {state.message}</div>;

  return (
    <>
      <h1>Clients &amp; engagements</h1>
      {state.orgs.length === 0 ? (
        <div className="card empty-state">
          <h3>You are not a member of any organisation</h3>
          <p>
            Organisations and memberships are provisioned by LINNFOSS — there
            is no self-service registration. If you expected access, contact
            LINNFOSS with the email address you signed in with.
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>Your role</th>
                <th>Member since</th>
              </tr>
            </thead>
            <tbody>
              {state.orgs.map((org) => (
                <tr key={org.id}>
                  <td>
                    <Link to={`/clients/${org.id}`}>{org.name}</Link>
                  </td>
                  <td>
                    <span className="badge badge-role">
                      {(() => { const r = state.roles.get(org.id); return r ? ROLE_LABEL[r] : "member"; })()}
                    </span>
                  </td>
                  <td>{new Date(org.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
